import { createPortal } from 'react-dom';
import { useState, type KeyboardEvent } from 'react';
import { erApiFeil } from '../api/feil.ts';
import { GRUPPER, HENDELSER, useOppdaterWebhook, type Hendelsesgruppe, type Webhook, type WebhookEventType } from '../api/useWebhooks.ts';
import { Dialog } from '../skall/Dialog.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Kode, useRad } from './Rad.tsx';

/**
 * Hendelsene i tre grupper (H3.5, konseptsiden skjerm 14–16): Innspill, Betaprogram og Hypoteser,
 * der Hypoteser bare finnes når rommet er skrudd på for leietakeren. Hver gruppe har én avkrysning
 * — tom, delvis (strek) eller alle — og antallet valgt. En gruppe med valg står åpen; de andre står
 * lukket med «Vis». 23 avkrysninger i én liste var ikke til å lese (avvik 16).
 *
 * Setningene er hendelsene; kontraktens navn står under bare med «Vis tekniske navn» (DD-50).
 */
export function Hendelsesgrupper({ valgte, onEndre, kanEndre, medHypoteser }: { valgte: readonly WebhookEventType[]; onEndre: (neste: WebhookEventType[]) => void; kanEndre: boolean; medHypoteser: boolean }) {
  const { tekniske } = useRad();
  const grupper = GRUPPER.filter((g) => medHypoteser || g.id !== 'hypoteser');
  const [aapne, setAapne] = useState<ReadonlySet<Hendelsesgruppe>>(() => new Set(grupper.filter((g) => HENDELSER.some((h) => h.gruppe === g.id && valgte.includes(h.navn))).map((g) => g.id)));
  const veksle = (g: Hendelsesgruppe) => setAapne((s) => (s.has(g) ? new Set([...s].filter((x) => x !== g)) : new Set([...s, g])));

  return (
    <div className="panel-hgrupper">
      {grupper.map((g) => {
        const alle = HENDELSER.filter((h) => h.gruppe === g.id);
        const valgt = alle.filter((h) => valgte.includes(h.navn));
        const tilstand: 'true' | 'false' | 'mixed' = valgt.length === 0 ? 'false' : valgt.length === alle.length ? 'true' : 'mixed';
        const aapen = aapne.has(g.id);
        const gruppeId = `hgr-${g.id}`;
        const velgAlle = () => {
          const uten = valgte.filter((e) => !alle.some((h) => h.navn === e));
          onEndre(tilstand === 'true' ? uten : [...uten, ...alle.map((h) => h.navn)]);
          if (tilstand !== 'true') setAapne((s) => new Set([...s, g.id]));
        };
        return (
          <div key={g.id} className="panel-hgruppe">
            <div className="panel-hgh">
              <button type="button" role="checkbox" aria-checked={tilstand} className="panel-hgh-cb" disabled={!kanEndre} onClick={velgAlle}>
                <span className="panel-hcb" data-tilstand={tilstand} aria-hidden="true">
                  {tilstand === 'true' ? <Ikon navn="checkmark-outline" /> : tilstand === 'mixed' ? <span className="panel-hcb-strek" /> : null}
                </span>
                <b>{g.navn}</b>
              </button>
              <span className="panel-hgh-antall" data-testid={`gruppe-antall-${g.id}`}>{`${valgt.length} av ${alle.length}`}</span>
              <button type="button" className="panel-knapp-lenke panel-hgh-vis" aria-expanded={aapen} aria-controls={gruppeId} aria-label={`${aapen ? 'Skjul' : 'Vis'} ${g.navn}`} onClick={() => veksle(g.id)}>
                {aapen ? 'Skjul' : 'Vis'}
              </button>
            </div>
            {aapen ? (
              <div id={gruppeId} className="panel-hgr">
                {alle.map((h) => (
                  <label key={h.navn} className="panel-hl">
                    <input type="checkbox" checked={valgte.includes(h.navn)} disabled={!kanEndre} onChange={(e) => onEndre(e.target.checked ? [...valgte, h.navn] : valgte.filter((x) => x !== h.navn))} data-testid={`webhook-hendelse-${h.navn}`} />
                    <span>
                      {h.gjelder}
                      {tekniske ? <Kode>{h.navn}</Kode> : null}
                    </span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
      {medHypoteser ? <p className="panel-hfast">Varslene sier aldri hva hypotesen handler om — bare nummeret, statusen, datoene, en lenke og hvem som skal varsles.</p> : null}
    </div>
  );
}

const urlKort = (url: string) => url.replace(/^https:\/\//i, '');
const likt = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

/**
 * «Endre hendelser» på en mottaker som finnes (skjerm 14, 16; avvik 14). `PATCH events` alene —
 * hemmeligheten og verifiseringen står (`WebhookUpdate`). Over 900 px står valget i kortet; under er
 * det et ark med «Lagre hendelsene» i tommelsonen (DD-47, DD-51). Foten teller det som ikke er lagret.
 * Feiler lagringen, står valgene, med «Prøv igjen».
 */
export function EndreHendelser({ w, medHypoteser, onLukk, onLagret }: { w: Webhook; medHypoteser: boolean; onLukk: () => void; onLagret: (tekst: string) => void }) {
  const smal = useSmal();
  const oppdater = useOppdaterWebhook();
  const [valgte, setValgte] = useState<WebhookEventType[]>(() => [...w.events]);
  const [feil, setFeil] = useState<string | null>(null);
  const endringer = HENDELSER.filter((h) => valgte.includes(h.navn) !== w.events.includes(h.navn)).length;
  const grunn = valgte.length === 0 ? 'Velg minst én hendelse.' : likt(valgte, w.events) ? 'Ingen endringer ennå.' : oppdater.isPending ? 'Lagrer …' : null;

  const lagre = () => {
    if (grunn) return;
    setFeil(null);
    oppdater
      .mutateAsync({ id: w.id, body: { events: valgte } })
      .then(() => onLagret(`Hendelsene for ${urlKort(w.url)} er lagret.`))
      .catch((e: unknown) => setFeil(erApiFeil(e) && e.code === 'room_not_enabled' ? e.brukertekst : 'Hendelsene ble ikke lagret. Valgene står.'));
  };
  const paaTast = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      lagre();
    } else if (e.key === 'Escape' && !smal) {
      e.preventDefault();
      e.stopPropagation();
      onLukk();
    }
  };

  const innhold = (
    <>
      <Hendelsesgrupper valgte={valgte} onEndre={setValgte} kanEndre medHypoteser={medHypoteser} />
      {feil ? (
        <p role="alert" className="panel-hred-feil">
          <Ikon navn="warning-outline" />
          <span>{feil}</span>
          <button type="button" className="panel-knapp-lenke" onClick={lagre}>
            Prøv igjen
          </button>
        </p>
      ) : null}
    </>
  );
  const fot = (
    <div className="panel-hred-fot">
      <button type="button" className="panel-knapp" aria-disabled={grunn ? true : undefined} aria-describedby={grunn ? `hred-grunn-${w.id}` : undefined} aria-busy={oppdater.isPending || undefined} aria-keyshortcuts="Control+Enter" onClick={lagre} data-testid={`hendelser-lagre-${w.id}`}>
        Lagre hendelsene
        {smal ? null : <kbd>Ctrl ↵</kbd>}
      </button>
      <button type="button" className="panel-knapp-lenke" onClick={onLukk}>
        Lukk {smal ? null : <kbd>Esc</kbd>}
      </button>
      <span id={`hred-grunn-${w.id}`} className="panel-hred-las">
        {endringer > 0 ? `${endringer} ${endringer === 1 ? 'endring er' : 'endringer er'} ikke lagret` : grunn}
      </span>
    </div>
  );

  if (smal) {
    return (
      createPortal(
        <Dialog tittelId={`hred-tittel-${w.id}`} onLukk={onLukk} className="h-ark panel-hred-ark" testid={`hendelser-ark-${w.id}`}>
          <div className="panel-hred" onKeyDown={paaTast}>
            <div>
              <h2 id={`hred-tittel-${w.id}`} className="panel-h2">
                Hendelser
              </h2>
              <code className="panel-hred-url">{urlKort(w.url)}</code>
            </div>
            {innhold}
            {fot}
          </div>
        </Dialog>,
        document.body,
      )
    );
  }
  return (
    <div className="panel-hred" role="group" aria-label={`Hendelser for ${urlKort(w.url)}`} onKeyDown={paaTast} data-testid={`hendelser-${w.id}`}>
      {innhold}
      {fot}
    </div>
  );
}
